import React from "react";
import {
  RainDecisionRequest,
  RainDecisionResponse,
} from "../../services/rainApi";
import JsonBlock from "./JsonBlock";

interface Props {
  result: RainDecisionResponse;
  options: string[];
  question: string;
  rawRequest: RainDecisionRequest | null;
  onNewRequest: () => void;
}

/** finding_summary arrives as HTML from the backend - render it as plain text. */
const stripHtml = (html: string): string => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

const ResultView: React.FC<Props> = ({
  result,
  options,
  question,
  rawRequest,
  onNewRequest,
}) => {
  const decision = result.decision || {};
  const selected =
    typeof decision.selected_option === "number"
      ? decision.selected_option
      : null;
  const noDecision = selected === null || selected < 0;
  const confidence =
    typeof decision.confidence_score === "number"
      ? Math.round(decision.confidence_score * 100)
      : null;

  return (
    <div className="rw-result">
      <div
        className={`rw-result-banner ${
          noDecision ? "rw-result-open" : "rw-result-decided"
        }`}
      >
        <div className="rw-result-kicker">
          {noDecision ? "Market stays open" : "Decision"}
        </div>
        <div className="rw-result-headline">
          {noDecision
            ? String(decision.selected_option_text || "Too early to determine")
            : options[selected as number] || `Outcome ${(selected as number) + 1}`}
        </div>
        {confidence !== null && (
          <div className="rw-result-confidence">
            Confidence: {confidence}%
          </div>
        )}
        {noDecision && (
          <div className="rw-result-note">
            This is a deliberate ruling, not a failure - the event has not
            been decided yet, so the market cannot be settled.
          </div>
        )}
      </div>

      <div className="rw-review-block">
        <div className="rw-review-label">Question</div>
        <div className="rw-review-value">{question}</div>
      </div>

      <div className="rw-review-block">
        <div className="rw-review-label">Outcomes</div>
        <ol className="rw-review-options">
          {options.map((option, i) => (
            <li
              key={i}
              className={i === selected ? "rw-option-selected" : undefined}
            >
              {option}
              {i === selected && (
                <span className="rw-selected-badge">Selected</span>
              )}
            </li>
          ))}
        </ol>
      </div>

      {decision.finding_summary && (
        <div className="rw-review-block">
          <div className="rw-review-label">Why</div>
          <div className="rw-review-value rw-result-summary">
            {stripHtml(String(decision.finding_summary))}
          </div>
        </div>
      )}

      {noDecision && (
        <p className="rw-hint">
          Submit again after the event concludes, or add conclusive evidence
          if you believe it already has.
        </p>
      )}

      {(decision.gpt_analysis || decision.perplexity_analysis) && (
        <details className="rw-details">
          <summary>How the judges saw it</summary>
          {decision.gpt_analysis && (
            <div className="rw-review-block">
              <div className="rw-review-label">Research agent A</div>
              <div className="rw-review-value rw-agent-text">
                {String(decision.gpt_analysis)}
              </div>
            </div>
          )}
          {decision.perplexity_analysis && (
            <div className="rw-review-block">
              <div className="rw-review-label">Research agent B</div>
              <div className="rw-review-value rw-agent-text">
                {String(decision.perplexity_analysis)}
              </div>
            </div>
          )}
        </details>
      )}

      <details className="rw-details">
        <summary>Developer view - raw request &amp; response</summary>
        <p className="rw-hint">
          Exactly what was sent and received - the same contract the Rain
          backend uses in production.
        </p>
        {rawRequest && <JsonBlock label="Request" value={rawRequest} />}
        <JsonBlock label="Response" value={result} />
      </details>

      {decision.dispute_id && (
        <div className="rw-result-meta">Reference: {decision.dispute_id}</div>
      )}

      <div className="rw-actions">
        <button type="button" className="rw-primary-btn" onClick={onNewRequest}>
          Start a new request
        </button>
      </div>
    </div>
  );
};

export default ResultView;
